import type { Product } from 'apps/commerce/types.ts'
import calcOFF from '$store/sdk/calcOFF.ts'
import { useOffer } from '$store/sdk/useOffer.ts'
import { formatPrice } from '$store/sdk/format.ts'

import AddToCartButtonVTEX from '$store/islands/AddToCartButton/vtex.tsx'
import WishlistButton from '$store/components/wishlist/WishlistButton.tsx'
import { useSignal } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import ScarcityBadge from 'deco-sites/dabelleultimate/islands/ScarcityBadge.tsx'

interface Props {
  product: Product
  hide?: {
    installments?: boolean
    ratings?: boolean
    productQuantityCartAndShipping?: boolean
  }
  textFixedBuy?: string
  textTemplate?: string
}

const QUANTITY_MAX_VALUE = 99

export default function ProductBuyFixed({
  product,
  hide,
  textTemplate,
}: Props) {
  const { offers, name = '', productID, isVariantOf } = product
  const { price = 0, listPrice, seller, pureInstallments } = useOffer(offers)
  const quantity = useSignal(1)
  const showBuyFixed = useSignal(false)

  const productGroupID = isVariantOf?.productGroupID ?? ''
  const discount = price && listPrice ? listPrice - price : 0
  const billingDuration = pureInstallments?.billingDuration ?? 0
  const billingIncrement = pureInstallments?.billingIncrement ?? 0

  useEffect(() => {
    const mediaQuery = globalThis.matchMedia('(max-width: 1023px)')

    const updateVisibility = () => {
      showBuyFixed.value = mediaQuery.matches || self.scrollY > 400
    }

    updateVisibility()
    self.addEventListener('scroll', updateVisibility, { passive: true })
    self.addEventListener('resize', updateVisibility)
    mediaQuery.addEventListener('change', updateVisibility)

    return () => {
      self.removeEventListener('scroll', updateVisibility)
      self.removeEventListener('resize', updateVisibility)
      mediaQuery.removeEventListener('change', updateVisibility)
    }
  }, [])

  const handleChangeQuantity = (value: number) => {
    if (value > QUANTITY_MAX_VALUE || value < 1) return

    quantity.value = value
  }

  return (
    <div
      data-show={showBuyFixed.value ? 'true' : 'false'}
      class="w-full fixed left-0 bottom-0 pointer-events-none opacity-0 data-[show=true]:opacity-100 data-[show=true]:pointer-events-auto z-[21] bg-white transition-opacity"
    >
      <div
        class={'w-full h-6 bg-primary-600 flex justify-center items-center '}
        style="background-color: #DA1884"
      >
        <span class={'text-white text-xs font-bold font-tt-norms'}>
          FRETE GRÁTIS acima de 129,90 | 5%OFF no PIX
        </span>
      </div>

      <div class="container max-w-[1296px] w-full font-tt-norms h-[90px] px-5 lg:h-[55px]">
        <div class="flex w-full h-full items-center justify-between gap-3 lg:gap-6">
          <div class="flex flex-col lg:flex-row">
            {(listPrice ?? 0) > price && (
              <div class="flex lg:flex-col-reverse lg:mr-8 items-center lg:items-start">
                <span class="text-sm font-light text-neutral-3 line-through font-tt-norms">
                  {formatPrice(listPrice, offers!.priceCurrency!)}
                </span>
                {/*Offers mobile*/}
                {/* <span class='ml-2 lg:ml-0 w-[77px] h-[24px] bg-primary-400 flex items-center justify-center font-tt-norms text-xs font-bold text-white rounded-full'>
									-{calcOFF(listPrice ?? 0, price).toFixed(0)}% OFF
								</span> */}
              </div>
            )}
            <div class="flex flex-col lg:flex-row lg:items-start lg:flex-col">
              <span className="text-lg font-bold text-black font-tt-norms lg:whitespace-nowrap">
                {formatPrice(price * 0.95, offers!.priceCurrency!)}{' '}
                <span class="text-base">no pix (5% OFF)</span>
              </span>
              {/* <span class='mt-1 text-xs lg:text-sm font-bold text-[#595959] font-tt-norms whitespace-nowrap lg:px-4 '>
								{formatPrice(price, offers!.priceCurrency!)}
							</span> */}

              {!hide?.installments && (
                <span class="text-xs lg:text-sm text-[#595959] font-tt-norms font-bold whitespace-nowrap">
                  ou {formatPrice(price, offers!.priceCurrency!)} em{' '}
                  {billingDuration}x de {formatPrice(billingIncrement)} sem
                  juros
                </span>
              )}
            </div>
          </div>

          <div class={'w-full flex justify-end'}>
            {!hide?.productQuantityCartAndShipping && (
              <div class="max-lg:ml-auto lg:mr-6 lg:mr-14 max-w-[108px] lg:max-w-[334px] w-full flex items-center">
                <AddToCartButtonVTEX
                  name={name}
                  productID={productID}
                  productGroupID={productGroupID}
                  price={price}
                  discount={discount}
                  seller={seller ?? ''}
                  quantity={quantity.value}
                  class={
                    'lg:rounded-r-none h-[45px] w-full max-w-[108px] lg:max-w-[204px] rounded border border-none bg-client-primary hover:bg-client-primary-dark transition-colors font-medium text-white text-base'
                  }
                />

                <div
                  class={
                    'hidden lg:flex justify-center items-center rounded-r-[4px]  w-[130px] h-[45px] border border-l-0 border-neutral-2 overflow-hidden gap-3'
                  }
                >
                  <button
                    onClick={() => handleChangeQuantity(quantity.value - 1)}
                    class={
                      'flex items-center justify-center w-6 h-7 outline-none bg-transparent text-2xl text-neutral-2 font-bold'
                    }
                  >
                    -
                  </button>
                  <input
                    class="w-5 h-[45px] text-center text-neutral-2 text-sm outline-none bg-transparent"
                    type="number"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    max={QUANTITY_MAX_VALUE}
                    min={1}
                    value={quantity}
                    onBlur={(e) =>
                      handleChangeQuantity?.(e.currentTarget.valueAsNumber)
                    }
                    maxLength={3}
                    size={3}
                  />
                  <button
                    class={
                      'flex items-center justify-center w-6 h-7 outline-none bg-transparent text-2xl text-neutral-2 font-bold'
                    }
                    onClick={() => handleChangeQuantity(quantity.value + 1)}
                  >
                    +
                  </button>
                </div>
              </div>
            )}

            {/* <WishlistButton product={product} /> */}
          </div>
        </div>
      </div>
      <div class="flex justify-center items-center mb-4 lg:hidden">
        <ScarcityBadge
          enabled
          mode="random"
          skuId={product?.productID}
          randomMin={4}
          randomMax={10}
          randomStep={1}
          randomStrategy="oncePerSession"
          persistTtlMinutes={45}
          textTemplate={textTemplate || 'Faltam apenas {{n}} itens disponíveis'}
          icon="alarm"
          color="#DA1884"
          bgColor="transparent"
          fontSize={14}
          fontWeight={500}
          dwellEnabledOnce
          dwellAfterSeconds={30}
          dwellDecrementStep={1}
        />
      </div>
    </div>
  )
}
